import { IFlowViewModel } from './models/i-flow-view-model';
import { IConnectionViewModel } from './models/i-connection-view-model';
import { GuidExtensions } from '@foblex/core';
import { INodeViewModel } from './models/i-node-view-model';
import { CreateConnectionRequest } from './models/requests/create-connection.request';
import { BulkRemoveRequest } from './models/requests/bulk-remove.request';
import { ReassignConnectionRequest } from './models/requests/reassign-connection.request';
import { EventsFlowDataFormService } from '../../../events-flow/services/events-flow-data-form.service';
import { hasEventsFlowEventNodeTypes } from '../../../events-flow/edit/models/events-flow-event-node-types';

export class FFlowService<T> {
  constructor(private readonly _eventsFlowDataFormService: EventsFlowDataFormService) {}

  public createConnection(request: CreateConnectionRequest<T>): void {
    request.flow.connections.push(this.createConnectionObject(request.outputKey, request.inputKey, ''));
  }

  public reassignConnection(request: ReassignConnectionRequest<T>): void {
    const connection = request.flow.connections.find(
      (x: IConnectionViewModel) => x.from === request.outputKey && x.to === request.oldInputKey,
    );
    if (connection) {
      connection.to = request.newInputKey;
    }
  }

  public bulkRemove(request: BulkRemoveRequest<T>) {
    let connectionKeys: string[] = request.connectionKeys || [];

    request.nodeKeys.forEach(x => {
      const keys = this.findAllConnectionsForNode(request.flow, x);
      connectionKeys.push(...keys);

      this.removeNode(request.flow, x);
    });

    const outputKeys = connectionKeys
      .map(x => {
        return this.removeConnection(request.flow, x);
      })
      .filter(x => x !== undefined) as string[];
  }

  public isAllNodesConnected(flow: IFlowViewModel<T>): boolean {
    console.log('isAllNodesConnected');
    const nodes = flow.nodes;
    const connections = flow.connections;

    // Create a map of output value to node key
    const outputToNodeKey = new Map<string, string>();
    nodes.forEach(node => {
      node.outputs.forEach(output => {
        outputToNodeKey.set(output, node.key);
      });
    });

    // Create an adjacency list for the graph
    const adjacencyList = new Map<string, Set<string>>();

    nodes.forEach(node => {
      adjacencyList.set(node.key, new Set());
    });

    connections.forEach(connection => {
      const fromNodeKey = outputToNodeKey.get(connection.from);
      const toNodeKey = nodes.find(node => node.input === connection.to)?.key;

      if (fromNodeKey && toNodeKey) {
        adjacencyList.get(fromNodeKey)!.add(toNodeKey);
        adjacencyList.get(toNodeKey)!.add(fromNodeKey); // Since it's undirected
      }
    });

    // Function to perform DFS
    const visited = new Set<string>();

    function dfs(nodeKey: string) {
      visited.add(nodeKey);
      adjacencyList.get(nodeKey)?.forEach(neighbor => {
        if (!visited.has(neighbor)) {
          dfs(neighbor);
        }
      });
    }

    // Start DFS from any node (we'll use the first node)
    if (nodes.length > 0) {
      dfs(nodes[0].key);
    }

    // Check if all nodes were visited
    return nodes.length === visited.size;
  }

  public findRootNode(flow: IFlowViewModel<T>): INodeViewModel<T> | null {
    const nodes = flow.nodes;
    const connections = flow.connections;

    // Create a set to track nodes that have incoming connections
    const nodesWithIncomingConnections = new Set<string>();

    // Populate the set with nodes that have an incoming connection
    connections.forEach(connection => {
      const targetNode = nodes.find(node => node.input === connection.to);
      if (targetNode) {
        nodesWithIncomingConnections.add(targetNode.key);
      }
    });

    // The root node will be the one that is not in the set of nodes with incoming connections
    const rootNode = nodes.find(node => !nodesWithIncomingConnections.has(node.key));

    // Return the root node, or null if there is no clear root
    return rootNode || null;
  }

  public getAllConnectedChildNodes<T>(startNodeKey: string, flow: IFlowViewModel<T>): Array<INodeViewModel<T>> {
    const nodes = flow.nodes;
    const connections = flow.connections;

    // Create a map of output value to node key
    const outputToNodeKey = new Map<string, string>();
    nodes.forEach(node => {
      node.outputs.forEach(output => {
        outputToNodeKey.set(output, node.key);
      });
    });

    // Create a map of node key to node for quick lookup
    const nodeMap = new Map<string, INodeViewModel<T>>();
    nodes.forEach(node => {
      nodeMap.set(node.key, node);
    });

    // Create an adjacency list
    const adjacencyList = new Map<string, Set<string>>();

    nodes.forEach(node => {
      adjacencyList.set(node.key, new Set());
    });

    connections.forEach(connection => {
      const fromNodeKey = outputToNodeKey.get(connection.from);
      const toNodeKey = nodes.find(node => node.input === connection.to)?.key;

      if (fromNodeKey && toNodeKey) {
        adjacencyList.get(fromNodeKey)!.add(toNodeKey);
      }
    });

    // DFS to collect all connected child nodes
    const visited = new Set<string>();
    const result: Array<INodeViewModel<T>> = [];

    function dfs(nodeKey: string) {
      visited.add(nodeKey);
      const currentNode = nodeMap.get(nodeKey);
      if (currentNode) {
        result.push(currentNode);
      }
      adjacencyList.get(nodeKey)?.forEach(neighbor => {
        if (!visited.has(neighbor)) {
          dfs(neighbor);
        }
      });
    }

    // Start DFS from the given node key
    if (nodeMap.has(startNodeKey)) {
      dfs(startNodeKey);
    }

    // Remove the start node itself from the result if it was added
    return result.filter(node => node.key !== startNodeKey);
  }

  public removeDisconnectedConnections<T>(
    nodes: Array<INodeViewModel<T>>,
    connections: Array<IConnectionViewModel>,
  ): Array<IConnectionViewModel> {
    // Create a set of all valid output and input values from the nodes
    const validOutputs = new Set<string>();
    const validInputs = new Set<string>();

    nodes.forEach(node => {
      node.outputs.forEach(output => validOutputs.add(output));
      if (node.input) {
        validInputs.add(node.input);
      }
    });

    // Filter connections that are properly connected
    const filteredConnections = connections.filter(connection => {
      return validOutputs.has(connection.from) && validInputs.has(connection.to);
    });

    return filteredConnections;
  }

  findParentNode<T>(childNodeKey: string, flow: IFlowViewModel<T>): INodeViewModel<T> | null {
    const nodes = flow.nodes;
    const connections = flow.connections;

    // Find the child node
    const childNode = nodes.find(node => node.key === childNodeKey);

    if (!childNode || !childNode.input) {
      return null; // If child node doesn't exist or doesn't have an input
    }

    // Find the connection that connects to the child node's input
    const connection = connections.find(conn => conn.to === childNode.input);

    if (!connection) {
      return null; // No connection found that connects to this child node
    }

    // Find the node that has the matching output
    const parentNode = nodes.find(node => node.outputs.includes(connection.from));

    return parentNode || null; // Return the parent node, or null if not found
  }

  private findAllConnectionsForNode(flow: IFlowViewModel<T>, key: string): string[] {
    const node = flow.nodes.find(x => x.key === key);

    let result: string[] = [];

    if (node) {
      node.outputs.forEach(output => {
        const connections = flow.connections.filter(c => c.from === output).map(c => c.key);
        if (connections.length) {
          result.push(...connections);
        }
      });
      const connections = flow.connections.filter(c => c.to === node.input).map(c => c.key);
      if (connections.length) {
        result.push(...connections);
      }
    }

    return result;
  }

  private removeConnection(flow: IFlowViewModel<T>, key: string): string | undefined {
    const index = flow.connections.findIndex((x: IConnectionViewModel) => x.key === key);
    if (index === -1) {
      return undefined;
    }
    const result = flow.connections[index].from;

    if (index > -1) {
      flow.connections.splice(index, 1);
    }
    return result;
  }

  private removeNode(flow: IFlowViewModel<any>, key: string): void {
    const index = flow.nodes.findIndex(y => y.key === key);
    if (index > -1) {
      const node = flow.nodes.find(y => y.key === key);

      if (hasEventsFlowEventNodeTypes(node.type)) {
        this._eventsFlowDataFormService.resetEventFlowEventMainData();
      } else {
        this._eventsFlowDataFormService.removeEventFlowActionItemForm(node.value.uniqueKey);
      }

      flow.nodes.splice(index, 1);
    }
  }

  private getOutputNode(flow: IFlowViewModel<T>, outputKey: string): INodeViewModel<T> {
    const result = flow.nodes.find(x => {
      return x.outputs.some(y => y === outputKey);
    });

    if (!result) {
      throw new Error('Node not found');
    }

    return result;
  }

  private createConnectionObject(outputKey: string, inputKey: string, outputName: string): IConnectionViewModel {
    const result: IConnectionViewModel = {
      key: GuidExtensions.generate(),
      from: outputKey,
      to: inputKey,
      name: outputName,
    };

    return result;
  }
}
